import FontAwesome from "@expo/vector-icons/FontAwesome";
import { DefaultTheme, ThemeProvider } from "@react-navigation/native";
import { useFonts } from "expo-font";
import { Slot, useNavigationContainerRef, usePathname } from "expo-router";
import { SplashScreen } from "expo-router";
import { useEffect, useMemo } from "react";
import { GluestackUIProvider } from "@/components/ui/gluestack-ui-provider";
import "../global.css";
import { SessionProvider, useAccessTokenClaims } from "@/utils/session.ctx";
import { useSplash } from "@/utils/useSplash";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import React from "react";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { expoLanguageDetector } from "@/utils/language.detector";
import { ToastMessageBusHandler } from "@/screens/common/toast";
import { TOAST_MESSAGE_BUS } from "@/screens/common/toast.consts";
import { usePostHog, PostHogProvider } from "posthog-react-native";
import { AttributionProvider } from "@/screens/common/attribution.provider";
import * as Sentry from "@sentry/react-native";
import { isRunningInExpoGo } from "expo";
import { Platform } from "react-native";

export {
  // Catch any errors thrown by the Layout component.
  ErrorBoundary,
} from "expo-router";

const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: !isRunningInExpoGo(),
});

Sentry.init({
  dsn: "https://79f7cc0245c1b09e59322039f07f5d74@o4507568059187200.ingest.de.sentry.io/4509385216753744",
  tracesSampleRate: 1.0, // Set tracesSampleRate to 1.0 to capture 100% of transactions for tracing. Adjusting this value in production.
  integrations: [
    // Pass integration
    navigationIntegration,
  ],
  enableNativeFramesTracking: !isRunningInExpoGo(), // Tracks slow and frozen frames in the application
});

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .use(expoLanguageDetector)
  .init({
    // the translations
    // (tip move them in a JSON file and import them,
    // or even better, manage them via a UI: https://react.i18next.com/guides/multiple-translation-files#manage-your-translations-with-a-management-gui)
    resources: {},
    fallbackLng: "en",
    supportedLngs: ["en"],
    nonExplicitSupportedLngs: true,
    interpolation: {
      escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
    },
  });

// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync();

export default function RootLayout() {
  const { isSplashActive, signalSplash } = useSplash();
  const [fontsLoaded, error] = useFonts({
    SpaceMono: require("../assets/fonts/SpaceMono-Regular.ttf"),
    ...FontAwesome.font,
  });

  // const colorScheme: ColorSchemeName = useColorScheme();

  const ref = useNavigationContainerRef();

  useEffect(() => {
    if (ref?.current) {
      navigationIntegration.registerNavigationContainer(ref);
    }
  }, [ref]);

  // Expo Router uses Error Boundaries to catch errors in the navigation tree.
  useEffect(() => {
    if (error) {
      console.error("Unable to load fonts", error);
      throw error;
    }
  }, [error]);

  useEffect(() => {
    if (fontsLoaded) {
      signalSplash({
        fontsLoaded: true,
      });
    }
  }, [fontsLoaded]);

  useEffect(() => {
    if (!isSplashActive) {
      SplashScreen.hideAsync();
    }
  }, [isSplashActive]);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (e, query) => {
            if (!query.meta?.noSystemError) {
              TOAST_MESSAGE_BUS.publish("ERROR", e);
            }
          },
        }),
        mutationCache: new MutationCache({
          onError: (e, _, __, mutation) => {
            if (!mutation.meta?.noSystemError) {
              TOAST_MESSAGE_BUS.publish("ERROR", e);
            }
          },
        }),
      }),
    []
  );

  if (!fontsLoaded) {
    return null;
  }

  return (
    <PostHogProvider
      apiKey="phc_LU87kMAKHVt4TA66OxlnFt7NplC22TfDadBg8YwzLr0"
      options={{
        host: "https://eu.i.posthog.com",
        defaultOptIn: Platform.OS !== "web",
      }}
      autocapture
    >
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <PosthogIdentificationProvider />
          {/* {(colorScheme ?? "light") as "light" | "dark"} */}
          <GluestackUIProvider mode="light">
            {/* {colorScheme === "dark" ? DarkTheme : DefaultTheme} */}
            <ThemeProvider value={DefaultTheme}>
              <ToastMessageBusHandler>
                <AttributionProvider>
                  <Slot />
                </AttributionProvider>
              </ToastMessageBusHandler>
            </ThemeProvider>
          </GluestackUIProvider>
        </SessionProvider>
      </QueryClientProvider>
    </PostHogProvider>
  );
}

function PosthogIdentificationProvider() {
  const claims = useAccessTokenClaims();
  const pathname = usePathname();
  const posthog = usePostHog();

  useEffect(() => {
    if (claims) {
      const { userId, ...rest } = claims.externalClaims;

      posthog.identify(userId, {
        ...rest,
        authId: claims.sub,
      });
    }
  }, [claims, posthog]);

  useEffect(() => {
    if (pathname && posthog) {
      posthog.screen(pathname);
    }
  }, [pathname, posthog]);

  return null;
}
